import { type IVariantSummary } from "~/hooks/useVariantTableData"

export type GroupedFieldOptions = { relevant: string[]; other: string[] }

export const RELEVANT_GROUP_LABEL = "Relevant"
export const OTHER_GROUP_LABEL = "Other options"

// Collects the set of values for `fieldValue` actually present in `contextVariants`.
// Mirrors the field-extraction logic of `applyFilter` (utils/filter.tsx).
export function collectRelevantValues(
  fieldValue: string,
  contextVariants: IVariantSummary[],
): Set<string> {
  const relevant = new Set<string>()
  const isExtraData = fieldValue.includes("(extradata)")
  const isIncomingOrderExtraData = fieldValue.includes("(incoming-order-extradata)")
  const extraDataKey = fieldValue
    .replace("(extradata)", "")
    .replace("(incoming-order-extradata)", "")

  for (const variant of contextVariants) {
    if (fieldValue === "vendors") {
      for (const v of variant.vendors ?? []) {
        if (v?.name) relevant.add(v.name)
      }
    } else if (fieldValue === "preferredVendorId") {
      // Mirror the resolver in filter.tsx (id → name on the variant's own vendors[],
      // fall back to id when the vendor is no longer associated).
      const id = variant.settings?.preferredVendorId
      if (id) {
        const name = variant.vendors?.find((v) => v.id === id)?.name ?? id
        relevant.add(String(name))
      }
    } else if (fieldValue === "lifecycleStatus") {
      const s = variant.settings?.lifecycleStatus
      relevant.add(s === "phasingOut" ? "Phasing out" : s === "phasedOut" ? "Phased out" : "Active")
    } else if (fieldValue === "tags") {
      for (const t of variant.tags ?? []) {
        if (t?.name) relevant.add(t.name)
      }
    } else if (isIncomingOrderExtraData) {
      const orders = variant.incomingOrders ?? []
      for (const order of orders) {
        const v = (order.extraData as Record<string, unknown> | undefined)?.[extraDataKey]
        if (v != null) relevant.add(String(v))
      }
    } else if (isExtraData) {
      const v = (variant.extraData as Record<string, unknown> | undefined)?.[extraDataKey]
      if (v != null) relevant.add(String(v))
    } else {
      const v = (variant as unknown as Record<string, unknown>)[fieldValue]
      if (v != null) relevant.add(String(v))
    }
  }

  return relevant
}

// Splits `allOptions` into options that are present in `relevantSet` ("relevant")
// and the rest ("other"). Source order is preserved within each bucket so the
// dropdown order matches the non-grouped path.
export function splitGroupedOptions(
  allOptions: string[],
  relevantSet: Set<string>,
): GroupedFieldOptions {
  const relevant: string[] = []
  const other: string[] = []
  for (const opt of allOptions) {
    if (relevantSet.has(opt)) relevant.push(opt)
    else other.push(opt)
  }
  return { relevant, other }
}
